/* eslint-disable @next/next/no-img-element */
/* eslint-disable jsx-a11y/anchor-is-valid */

'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import Image from 'next/image';
import {
  Container, DropdownMenu, Dropdown, DropdownToggle,
} from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { useAppDispatch, useAppSelector } from '../../../lib/hooks';
import { getGeoIp, updateGeoIp } from '../../../services/users.service';

// Actions
import {
  selectIsLoggedIn, selectToken, logoutAction,
} from '../../../lib/features/auth/authSlice';
import { selectCart, updateCartAction } from '../../../lib/features/cart/cartSlice';
import { isTokenExpired } from '../../../services/auth.service';

// import images
import logoDark from '../../../assets/images/logo-dark.png';
import logoLight from '../../../assets/images/logo-light.png';

function NavBar(props) {
  const { hasDarkTopBar } = props;
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const token = useAppSelector(selectToken);

  const cart = useAppSelector(selectCart);

  const [dropdownOpenShop, setDropdownOpenShop] = useState(false);
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  const router = useRouter();

  const activateMenu = () => {
    if (typeof window === 'undefined') return;

    const menuItems = document.getElementsByClassName('sub-menu-item');
    if (menuItems) {
      for (let idx = 0; idx < menuItems.length; idx += 1) {
        if (window.location.href.includes(menuItems[idx].href)) {
          // if (menuItems[idx].href === window.location.href) {
          menuItems[idx].classList.add('active');
          const immediateParent = menuItems[idx].closest('li');
          if (immediateParent) {
            immediateParent.classList.add('active');
          }
        } else {
          menuItems[idx].classList.remove('active');
          const immediateParent = menuItems[idx].closest('li');
          if (immediateParent) {
            immediateParent.classList.remove('active');
          }
        }
      }
    }
  };

  const initMenu = () => {
    activateMenu();
  };

  const scrollNavigation = () => {
    if (typeof window === 'undefined') return;

    const navbar = document.getElementById('topnav');
    if (navbar != null) {
      if (
        document.body.scrollTop >= 50
        || document.documentElement.scrollTop >= 50
      ) {
        navbar.classList.add('nav-sticky');
      } else {
        navbar.classList.remove('nav-sticky');
      }
    }
  };

  useEffect(() => {
    // The hydration gate has to flip after mount by definition — server and first
    // client render must match, so this cannot be hoisted out of the effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);

    // Update cart in cookies on first load
    if (isLoggedIn) {
      try {
        if (isTokenExpired(token)) {
          dispatch(logoutAction());
          // Redirect to login with expiry message
          if (typeof window !== 'undefined') {
            const returnUrl = encodeURIComponent(window.location.pathname);
            router.push(`/auth-login?expired=true&from=${returnUrl}`);
          }
          return;
        }
        dispatch(updateCartAction({ token }));
      } catch (err) {
        console.error(err.message);
      }
      // Geo-IP is telemetry, and telemetry is not allowed to end a session.
      //
      // This used to log the user out and push him to /auth-login on a 401 from
      // this call. It runs on every mount of the navbar — that is, on every page,
      // including /shop-checkouts — so any 401 from a side-channel write threw
      // the person out mid-flow. And a 401 here does not only mean "the token
      // expired": a backend restart, a JWT secret that no longer matches, a
      // half-applied migration all produce one just as readily. The session had
      // already been judged above by isTokenExpired; a geo write has nothing to
      // add to that verdict, and gets no say in it.
      //
      // If the token really is dead, the next call that matters will say so and
      // handle it. Losing a stale session a few minutes later costs nothing;
      // losing a customer on the checkout page costs a sale.
      const asyncget = async () => {
        try {
          const ip = await getGeoIp();
          await updateGeoIp(ip.IPv4, token);
        } catch (error) {
          console.error('geo-ip update failed:', error.message);
        }
      };
      asyncget();
    }

    if (typeof window !== 'undefined') {
      initMenu();
      window.addEventListener('scroll', scrollNavigation, true);
      // Defer scrollNavigation to avoid hydration mismatch
      requestAnimationFrame(() => scrollNavigation());
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', scrollNavigation, true);
      }
    };
    // Mount-only on purpose: this checks the token once, attaches a single scroll
    // listener and initialises the menu. Re-running it on auth changes would
    // re-attach the listener and repeat the expiry redirect.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
    * Toogle menu in mobile screen
    */
  const isToogleMenu = () => {
    if (typeof window === 'undefined') return;

    const isToggle = document.getElementById('isToggle');
    if (isToggle) {
      isToggle.classList.toggle('open');
      const isOpen = document.getElementById('navigation');

      if (isOpen && isOpen.style.display === 'block') {
        isOpen.style.display = 'none';
      } else if (isOpen) {
        isOpen.style.display = 'block';
      }
    }
  };

  return (
    <header id="topnav" className="defaultscroll sticky">
      <Container>
        <div>
          {hasDarkTopBar ? (
            <Link className="logo" href="/">
              <Image
                src={logoDark.src}
                height={27}
                width={109}
                className="logo-light-mode"
                alt="logo light"
              />
              <Image
                src={logoLight.src}
                height={27}
                width={109}
                className="logo-dark-mode"
                alt="logo light"
              />
            </Link>
          ) : (
            <Link className="logo" href="/">
              <Image
                src={logoDark.src}
                height={27}
                width={109}
                className="l-dark"
                alt="logo"
              />
              <Image
                src={logoLight.src}
                height={27}
                width={109}
                className="l-light"
                alt="logo-light"
              />
              <Image
                src={logoLight.src}
                height={27}
                width={109}
                className="logo-dark-mode"
                alt="logo-dark"
              />
            </Link>
          )}
        </div>

        <div className="menu-extras">
          <div className="menu-item">
            <button
              type="button"
              className="navbar-toggle"
              id="isToggle"
              onClick={isToogleMenu}
              aria-label="menu"
            >
              <div className="lines">
                <span />
                <span />
                <span />
              </div>
            </button>
          </div>
        </div>

        {/* {cartAndAccount} */}
        {mounted && isLoggedIn ? (
          <ul className="buy-button list-inline mb-0">
            {cart?.length > 0
              ? (
                <li className="list-inline-item mb-0 pe-2">
                  <Dropdown
                    isOpen={dropdownOpenShop}
                    toggle={() => (setDropdownOpenShop(!dropdownOpenShop))}
                  >
                    <DropdownToggle
                      type="button"
                      id="buyButton"
                      className="btn btn-icon btn-pills btn-lg  dropdown-toggle shoppingbtn"
                      color="primary"
                    >
                      <FeatherIcon icon="shopping-cart" className="icons" />
                    </DropdownToggle>
                    <DropdownMenu
                      direction="end"
                      className="dd-menu dropdown-menu-end bg-white shadow rounded border-0 mt-3 p-4"
                      style={{ width: '300px' }}
                    >
                      <div className="pb-4">
                        {cart.map((item) => (
                          <Link
                            href={item.type === 'credits' ? '#' : `/shop/${item.path}`}
                            className="d-flex align-items-center"
                            key={item.id}
                            onClick={() => (setDropdownOpenShop(false))}
                          >
                            <img
                              src={item.image}
                              className="shadow rounded"
                              style={{ maxHeight: '64px' }}
                              alt={item.name}
                            />
                            <div className="flex-1 text-start ms-3">
                              <h6 className="text-dark mb-0">{item.name}</h6>
                            </div>
                            <h6 className="text-dark mb-0">
                              €
                              {item.price}
                            </h6>
                          </Link>
                        ))}
                      </div>

                      <div className="d-flex align-items-center justify-content-between pt-4 border-top">
                        <h6 className="text-dark mb-0">Total(€):</h6>
                        <h6 className="text-dark mb-0">
                          €
                          {' '}
                          {cart.reduce((sum, item) => (sum + +item.price), 0).toFixed(2)}
                        </h6>
                      </div>

                      <div className="mt-3 text-center">
                        <Link href="/shop-cart" className="btn btn-primary me-2" onClick={() => (setDropdownOpenShop(false))}>
                          View
                        </Link>
                        <Link href="/shop-checkouts" className="btn btn-primary" onClick={() => (setDropdownOpenShop(false))}>
                          Checkout
                        </Link>
                      </div>
                    </DropdownMenu>
                  </Dropdown>
                </li>
              )
              : null}
            <li className="list-inline-item mb-0">
              <Dropdown
                color="primary"
                isOpen={dropdownIsOpen}
                toggle={() => (setDropdownIsOpen(!dropdownIsOpen))}
              >
                <DropdownToggle
                  type="button"
                  color="primary"
                  id="buyButton"
                  className="btn btn-icon btn-pills btn-lg settingbtn"
                >
                  <FeatherIcon icon="user" className="icons" />
                  {/* <span className="position-absolute top-0 start-100
            translate-middle p-1 bg-danger border border-danger rounded-circle">
              <span className="visually-hidden" />
            </span> */}
                </DropdownToggle>
                <DropdownMenu
                  direction="start"
                  className="dd-menu dropdown-menu-end bg-white shadow rounded border-0 mt-3 py-3"
                  style={{ width: '200px' }}
                >
                  <Link className="dropdown-item" href="/profile/overview" onClick={() => (setDropdownIsOpen(false))}>
                    <i className="uil uil-user align-middle me-1" />
                    {' '}
                    Account
                  </Link>
                  <Link className="dropdown-item" href="/profile/purchases" onClick={() => (setDropdownIsOpen(false))}>
                    <i className="uil uil-shopping-bag align-middle me-1" />
                    {' '}
                    Purchases
                    {/* <span className="badge badge-important
              p-1 mx-5 bg-danger border border-danger rounded-circle">
                <span className="visually-hidden" />
              </span> */}
                  </Link>
                  <Link className="dropdown-item" href="/profile/conversions" onClick={() => (setDropdownIsOpen(false))}>
                    <i className="uil uil-exchange align-middle me-1" />
                    {' '}
                    Conversions
                  </Link>
                  <div className="dropdown-divider my-3 border-top" />
                  <Link
                    href="#"
                    className="dropdown-item"
                    onClick={() => {
                      dispatch(logoutAction());
                      router.push('/');
                    }}
                  >
                    <i className="uil uil-sign-out-alt align-middle me-1" />
                    {' '}
                    Logout
                  </Link>
                </DropdownMenu>
              </Dropdown>
            </li>
          </ul>
        ) : null}

        <div id="navigation">
          <ul className={hasDarkTopBar ? 'navigation-menu' : 'navigation-menu nav-light'}>
            {/* Coilmind first and in the accent colour. The home page block
                reaches only the 441 sessions a month that land on the home page;
                441 of 999 — the other 558 arrive from search straight onto a
                product, an article or the converter and never see it. The menu
                is the only thing every one of them passes.

                Accent and a pill, not a button: this bar is also on the cart and
                the checkout, where a loud link to a new product competes with
                finishing a purchase. */}
            <li>
              {/* Colours live in app/styles/Apps.scss, not here: which accent is
                  readable depends on a class set on an ancestor (nav-light for
                  the dark hero, nav-sticky once it turns white), and an inline
                  style cannot answer that question. */}
              <Link href="/coilmind" className="sub-menu-item nav-coilmind">
                Coilmind
                <span className="nav-coilmind-pill">NEW</span>
              </Link>
            </li>
            <li><Link href="/converter" className="sub-menu-item">Converter</Link></li>
            <li><Link href="/ai-generator" className="sub-menu-item">AI Widgets</Link></li>
            {/* The free symbol reference. Plain item, no accent: it is a
                navigation aid, and a menu link from every page is what gives
                the section its weight (SEO_GROWTH_PLAN.md §3). "Symbols", not
                "HMI Symbols": the longer label wrapped Sign Up onto a second
                row at 1366 px for signed-out visitors. */}
            <li><Link href="/hmi-symbols" className="sub-menu-item">Symbols</Link></li>
            <li><Link href="/blog" className="sub-menu-item">Blog</Link></li>
            <li><Link href="/shop" className="sub-menu-item">Shop</Link></li>
            {/* {signIn} */}
            {mounted && !isLoggedIn
              ? <li><Link href={`/auth-login?from=${encodeURIComponent(pathname)}`} className="sub-menu-item align-right">Sign In</Link></li>
              : null}
            {/* {signUp} */}
            {mounted && !isLoggedIn
              ? <li><Link href="/auth-signup" className="sub-menu-item">Sign Up</Link></li>
              : null}
          </ul>
        </div>
      </Container>
    </header>
  );
}

export default NavBar;
